import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/Logo';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { AgentThinking } from '@/components/agent/AgentThinking';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { 
  Building2, Briefcase, Utensils, Stethoscope, Code, ShoppingBag, 
  MoreHorizontal, ChevronRight, Sparkles, CheckCircle2
} from 'lucide-react';
import { z } from 'zod';

const orgNameSchema = z.string().min(2).max(100);

interface SwipeOnboardingProps {
  onComplete?: () => void;
}

const industries = [
  { id: 'agency', label: 'Agency / Studio', icon: Briefcase },
  { id: 'food', label: 'Food & Hospitality', icon: Utensils },
  { id: 'healthcare', label: 'Healthcare', icon: Stethoscope },
  { id: 'tech', label: 'Tech / SaaS', icon: Code },
  { id: 'retail', label: 'Retail / E-com', icon: ShoppingBag },
  { id: 'other', label: 'Other', icon: MoreHorizontal },
];

export function SwipeOnboarding({ onComplete }: SwipeOnboardingProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [summary, setSummary] = useState<{ roles: number; sops: number; runs: number } | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  const generateUniqueSlug = (name: string): string => {
    const baseSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    const uniqueSuffix = Date.now().toString(36).slice(-6);
    return `${baseSlug}-${uniqueSuffix}`;
  };

  const handleOrgSubmit = async () => {
    if (!orgNameSchema.safeParse(orgName).success) {
      toast({
        variant: 'destructive',
        title: 'Invalid name',
        description: 'Organization name must be 2-100 characters.',
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const slug = generateUniqueSlug(orgName);
      
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: orgName,
          slug,
          country: 'India',
          default_currency: 'INR',
          billing_email: user.email,
          plan_tier: 'launch',
          plan_seat_limit: 10,
        })
        .select('id')
        .single();

      if (orgError) throw orgError;
      
      setOrganizationId(org.id);
      setDirection(1);
      setStep(1);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to create organization.',
      });
    }
  };

  const handleIndustrySelect = async (selectedIndustry: string) => {
    setIndustry(selectedIndustry);
    setDirection(1);
    setStep(2);
    
    // Start AI generation
    setIsGenerating(true);
    
    try {
      if (!organizationId) throw new Error('No organization');

      // Save onboarding profile
      await supabase
        .from('onboarding_profiles')
        .insert({
          organization_id: organizationId,
          answers_json: { industry: selectedIndustry, orgName },
          processed_by_ai: false,
        });

      // Call AI copilot
      const { data, error } = await supabase.functions.invoke('ops-copilot', {
        body: {
          answers: {
            orgType: selectedIndustry,
            breakpoints: [],
            repeatedWorkflows: '',
          },
          organizationId,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setSummary(data.summary);
    } catch (error: any) {
      console.error('AI generation error:', error);
      // Still show success with fallback
      setSummary({ roles: 3, sops: 5, runs: 1 });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleComplete = () => {
    toast({
      title: 'Welcome to Ritual! 🎉',
      description: 'Your operations are ready.',
    });
    if (onComplete) {
      onComplete();
    } else {
      navigate('/app/feed');
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
    }),
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-center py-6">
        <Logo size="lg" />
      </div>

      {/* Progress */}
      <div className="flex justify-center gap-2 px-8 mb-8">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              'h-1 rounded-full transition-all duration-500',
              i <= step ? 'bg-primary flex-[2]' : 'bg-muted flex-1'
            )}
          />
        ))}
      </div>

      {/* Cards */}
      <div className="flex-1 px-4 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          {/* Step 0: Organization Name */}
          {step === 0 && (
            <motion.div
              key="org"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="h-full"
            >
              <div className="bg-card rounded-3xl border shadow-xl p-8 max-w-md mx-auto">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Let's set up in 60 seconds</h2>
                    <p className="text-muted-foreground">What's your company called?</p>
                  </div>
                </div>

                <Input
                  placeholder="e.g. Acme Inc"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="h-14 text-lg mb-8"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && orgName.length >= 2) {
                      handleOrgSubmit();
                    }
                  }}
                />

                <Button
                  variant="hero"
                  className="w-full h-14 text-base touch-target"
                  disabled={orgName.length < 2}
                  onClick={handleOrgSubmit}
                >
                  Continue
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 1: Industry */}
          {step === 1 && (
            <motion.div
              key="industry"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="h-full"
            >
              <div className="bg-card rounded-3xl border shadow-xl p-8 max-w-md mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold mb-2">What do you do?</h2>
                  <p className="text-muted-foreground">
                    Pick one. We'll customize your playbooks.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {industries.map((ind) => (
                    <button
                      key={ind.id}
                      onClick={() => handleIndustrySelect(ind.id)}
                      className={cn(
                        'flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all touch-target',
                        'hover:border-primary hover:bg-primary/5 active:scale-95',
                        industry === ind.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      )}
                    >
                      <ind.icon className="h-8 w-8 text-primary" />
                      <span className="text-sm font-medium text-center">{ind.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: AI Generation / Complete */}
          {step === 2 && (
            <motion.div
              key="complete"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="h-full"
            >
              <div className="bg-card rounded-3xl border shadow-xl p-8 max-w-md mx-auto">
                {isGenerating ? (
                  <div className="text-center py-12">
                    <AgentThinking 
                      message="Building your playbooks..." 
                      subMessage={`Analyzing ${industry} workflows`}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center mb-8">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', delay: 0.2 }}
                      >
                        <div className="h-24 w-24 rounded-full bg-success/10 flex items-center justify-center">
                          <CheckCircle2 className="h-12 w-12 text-success" />
                        </div>
                      </motion.div>
                    </div>

                    <h2 className="text-3xl font-bold text-center mb-3">
                      Ready to go.
                    </h2>
                    <p className="text-muted-foreground text-center mb-8 text-lg">
                      Your agent created:
                    </p>

                    {summary && (
                      <div className="grid grid-cols-3 gap-4 mb-10">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="text-center p-3 bg-muted rounded-xl"
                        >
                          <div className="text-2xl font-bold text-primary">{summary.roles}</div>
                          <div className="text-xs text-muted-foreground">Roles</div>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="text-center p-3 bg-muted rounded-xl"
                        >
                          <div className="text-2xl font-bold text-primary">{summary.sops}</div>
                          <div className="text-xs text-muted-foreground">Playbooks</div>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                          className="text-center p-3 bg-muted rounded-xl"
                        >
                          <div className="text-2xl font-bold text-primary">{summary.runs}</div>
                          <div className="text-xs text-muted-foreground">Tasks</div>
                        </motion.div>
                      </div>
                    )}

                    <Button
                      variant="hero"
                      className="w-full h-14 text-base touch-target"
                      onClick={handleComplete}
                    >
                      <Sparkles className="h-5 w-5 mr-2" />
                      Let's go
                    </Button>

                    <p className="text-sm text-center text-muted-foreground mt-6">
                      Swipe right to complete • left to skip
                    </p>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="py-6 text-center">
        <p className="text-xs text-muted-foreground">
          14-day free trial • No credit card required
        </p>
      </div>
    </div>
  );
}
